import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Sparkles, Users, Layers3 } from "lucide-react";

const stats = [
  { label: "Duration", value: "Tue → Fri", icon: Sparkles, testid: "stat-duration" },
  { label: "Eligibility", value: "Engineering", icon: Users, testid: "stat-eligibility" },
  { label: "Per Person", value: "1-3 Skills", icon: Layers3, testid: "stat-skills" },
];

function NodeGrid({ mouseX, mouseY }) {
  const cols = 9;
  const rows = 6;
  const nodes = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      nodes.push({ r, c, id: `${r}-${c}` });
    }
  }
  return (
    <svg viewBox="0 0 900 600" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid meet">
      <defs>
        <radialGradient id="node-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#2FCC7C" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#2FCC7C" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="line-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#2FCC7C" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#2D7BF0" stopOpacity="0.4" />
        </linearGradient>
      </defs>
      {/* connecting lines - horizontal & vertical */}
      {nodes.map((n) => {
        if (n.c < cols - 1) {
          return (
            <line
              key={`h-${n.id}`}
              x1={50 + n.c * 100}
              y1={50 + n.r * 100}
              x2={50 + (n.c + 1) * 100}
              y2={50 + n.r * 100}
              stroke="url(#line-grad)"
              strokeWidth="0.6"
            />
          );
        }
        return null;
      })}
      {nodes.map((n) => {
        if (n.r < rows - 1) {
          return (
            <line
              key={`v-${n.id}`}
              x1={50 + n.c * 100}
              y1={50 + n.r * 100}
              x2={50 + n.c * 100}
              y2={50 + (n.r + 1) * 100}
              stroke="url(#line-grad)"
              strokeWidth="0.6"
            />
          );
        }
        return null;
      })}
      {nodes.map((n, i) => (
        <motion.circle
          key={n.id}
          cx={50 + n.c * 100}
          cy={50 + n.r * 100}
          r={2.5}
          fill="#2FCC7C"
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 3 + (i % 5) * 0.4, repeat: Infinity, delay: (i % 7) * 0.2 }}
        />
      ))}
    </svg>
  );
}

function NeuralBrain({ mouseX, mouseY }) {
  const rx = useTransform(mouseY, [0, 1], [10, -10]);
  const ry = useTransform(mouseX, [0, 1], [-14, 14]);
  const rxS = useSpring(rx, { stiffness: 60, damping: 20 });
  const ryS = useSpring(ry, { stiffness: 60, damping: 20 });

  // Neural nodes positioned inside a stylized brain silhouette (viewBox 400x400)
  const nodes = [
    // left hemisphere
    { x: 145, y: 130 }, { x: 115, y: 175 }, { x: 105, y: 225 }, { x: 130, y: 270 },
    { x: 165, y: 300 }, { x: 175, y: 205 }, { x: 155, y: 165 }, { x: 140, y: 240 },
    // right hemisphere
    { x: 255, y: 130 }, { x: 285, y: 175 }, { x: 295, y: 225 }, { x: 270, y: 270 },
    { x: 235, y: 300 }, { x: 225, y: 205 }, { x: 245, y: 165 }, { x: 260, y: 240 },
    // central column (corpus callosum)
    { x: 200, y: 155 }, { x: 200, y: 205 }, { x: 200, y: 255 },
  ];

  // Curated connection pairs (indices)
  const edges = [
    [0,6],[6,5],[5,7],[7,4],[6,1],[1,2],[2,3],[3,4], // left web
    [8,14],[14,13],[13,15],[15,12],[14,9],[9,10],[10,11],[11,12], // right web
    [16,17],[17,18], // center
    [6,16],[14,16],[5,17],[13,17],[7,18],[15,18], // hemisphere bridges
    [0,8],[3,12], // top+bottom cross
  ];

  return (
    <motion.div
      style={{ perspective: 1000 }}
      className="relative w-full h-full flex items-center justify-center"
    >
      <motion.svg
        viewBox="0 0 400 400"
        className="w-[380px] h-[380px] lg:w-[460px] lg:h-[460px]"
        style={{
          rotateX: rxS,
          rotateY: ryS,
          transformStyle: "preserve-3d",
          filter: "drop-shadow(0 0 40px rgba(47,204,124,0.35))",
        }}
      >
        <defs>
          <radialGradient id="brainCore" cx="50%" cy="45%" r="50%">
            <stop offset="0%" stopColor="#2FCC7C" stopOpacity="0.18" />
            <stop offset="60%" stopColor="#2D7BF0" stopOpacity="0.06" />
            <stop offset="100%" stopColor="#000" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="edgeGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#2FCC7C" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#2D7BF0" stopOpacity="0.7" />
          </linearGradient>
          <filter id="neonBlur" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.5" />
          </filter>
        </defs>

        {/* soft core glow behind brain */}
        <circle cx="200" cy="215" r="140" fill="url(#brainCore)" />

        {/* Brain silhouette - two hemispheres */}
        <motion.g
          animate={{ scale: [1, 1.02, 1] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: "200px 215px" }}
        >
          {/* Left hemisphere outline */}
          <path
            d="M 195 90 C 130 90, 80 140, 80 210 C 80 275, 120 320, 175 335 C 200 340, 200 335, 200 320 L 200 100 C 200 92, 198 90, 195 90 Z"
            fill="none"
            stroke="#2FCC7C"
            strokeOpacity="0.35"
            strokeWidth="1.5"
          />
          {/* Right hemisphere outline */}
          <path
            d="M 205 90 C 270 90, 320 140, 320 210 C 320 275, 280 320, 225 335 C 200 340, 200 335, 200 320 L 200 100 C 200 92, 202 90, 205 90 Z"
            fill="none"
            stroke="#2D7BF0"
            strokeOpacity="0.35"
            strokeWidth="1.5"
          />
          {/* Convolutions (folds) - abstract squiggles */}
          <path d="M 105 165 Q 125 155 145 165 Q 165 175 155 195" fill="none" stroke="#2FCC7C" strokeOpacity="0.25" strokeWidth="1" />
          <path d="M 100 220 Q 130 215 150 225 Q 165 235 150 255" fill="none" stroke="#2FCC7C" strokeOpacity="0.25" strokeWidth="1" />
          <path d="M 120 285 Q 145 280 170 295" fill="none" stroke="#2FCC7C" strokeOpacity="0.25" strokeWidth="1" />
          <path d="M 295 165 Q 275 155 255 165 Q 235 175 245 195" fill="none" stroke="#2D7BF0" strokeOpacity="0.25" strokeWidth="1" />
          <path d="M 300 220 Q 270 215 250 225 Q 235 235 250 255" fill="none" stroke="#2D7BF0" strokeOpacity="0.25" strokeWidth="1" />
          <path d="M 280 285 Q 255 280 230 295" fill="none" stroke="#2D7BF0" strokeOpacity="0.25" strokeWidth="1" />
          {/* Central fissure */}
          <line x1="200" y1="100" x2="200" y2="330" stroke="#FFFFFF" strokeOpacity="0.12" strokeWidth="1" strokeDasharray="2 4" />
        </motion.g>

        {/* Connections */}
        {edges.map(([a, b], i) => {
          const A = nodes[a];
          const B = nodes[b];
          return (
            <g key={`e-${i}`}>
              <line
                x1={A.x} y1={A.y} x2={B.x} y2={B.y}
                stroke="url(#edgeGrad)"
                strokeWidth="0.9"
                strokeOpacity="0.4"
              />
              {/* animated pulse along the edge */}
              <motion.circle
                cx={A.x}
                cy={A.y}
                r="2.4"
                fill="#2FCC7C"
                filter="url(#neonBlur)"
                initial={{ cx: A.x, cy: A.y, opacity: 0 }}
                animate={{
                  cx: [A.x, B.x],
                  cy: [A.y, B.y],
                  opacity: [0, 1, 0],
                }}
                transition={{
                  duration: 2.6 + (i % 5) * 0.35,
                  repeat: Infinity,
                  delay: (i % 7) * 0.4,
                  ease: "easeInOut",
                }}
              />
            </g>
          );
        })}

        {/* Nodes */}
        {nodes.map((n, i) => (
          <g key={`n-${i}`}>
            <motion.circle
              cx={n.x}
              cy={n.y}
              r="6"
              fill={i % 3 === 0 ? "#2FCC7C" : i % 3 === 1 ? "#2D7BF0" : "#2FCC7C"}
              fillOpacity="0.35"
              filter="url(#neonBlur)"
              animate={{ scale: [1, 1.4, 1] }}
              transition={{ duration: 2.5 + (i % 4) * 0.3, repeat: Infinity, delay: (i % 6) * 0.2 }}
            />
            <circle
              cx={n.x}
              cy={n.y}
              r="2.4"
              fill={i % 3 === 0 ? "#2FCC7C" : i % 3 === 1 ? "#2D7BF0" : "#FFFFFF"}
            />
          </g>
        ))}

        {/* orbital ring */}
        <motion.circle
          cx="200"
          cy="215"
          r="170"
          fill="none"
          stroke="#2FCC7C"
          strokeOpacity="0.15"
          strokeWidth="1"
          strokeDasharray="3 8"
          animate={{ rotate: 360 }}
          transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
          style={{ transformOrigin: "200px 215px" }}
        />
      </motion.svg>

      {/* small orbiting dots outside the brain */}
      <motion.div
        className="absolute w-3 h-3 rounded-full bg-[#2FCC7C] glow-green-strong pointer-events-none"
        animate={{ x: [0, 200, 0, -200, 0], y: [-170, 0, 170, 0, -170] }}
        transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
      />
      <motion.div
        className="absolute w-2 h-2 rounded-full bg-[#2D7BF0] pointer-events-none"
        style={{ boxShadow: "0 0 20px #2D7BF0" }}
        animate={{ x: [0, -220, 0, 220, 0], y: [180, 0, -180, 0, 180] }}
        transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
      />
    </motion.div>
  );
}

export default function Hero() {
  const heroRef = useRef(null);
  const mouseX = useMotionValue(0.5);
  const mouseY = useMotionValue(0.5);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  // parallax + fade-out on scroll
  const contentY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const cubeY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const cubeScale = useTransform(scrollYProgress, [0, 1], [1, 0.55]);
  const gridY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const handleMouseMove = (e) => {
    const rect = heroRef.current?.getBoundingClientRect();
    if (!rect) return;
    mouseX.set((e.clientX - rect.left) / rect.width);
    mouseY.set((e.clientY - rect.top) / rect.height);
  };

  return (
    <section
      id="top"
      ref={heroRef}
      onMouseMove={handleMouseMove}
      className="relative min-h-[100vh] flex items-center overflow-hidden pt-28 pb-12 px-6 md:px-12 lg:px-24"
      data-testid="hero-section"
    >
      {/* faint node grid */}
      <motion.div style={{ y: gridY }} className="absolute inset-0 opacity-40 pointer-events-none">
        <NodeGrid mouseX={mouseX} mouseY={mouseY} />
      </motion.div>

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 w-full max-w-7xl mx-auto grid lg:grid-cols-[1.15fr_1fr] gap-16 items-center"
      >
        {/* LEFT */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mb-8"
            data-testid="hero-badge"
          >
            <span className="w-2 h-2 rounded-full bg-[#2FCC7C] animate-pulse" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-300">
              Internal Hackathon · Now Open
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black leading-[0.95] tracking-tighter"
          >
            CareStack{" "}
            <span className="text-gradient-neon glow-text-green">AI-Skill</span>
            <br />
            Challenge
            <span className="text-[#2FCC7C]">.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 text-lg md:text-xl text-zinc-400 max-w-xl leading-relaxed"
          >
            A challenge for the engineering team to build, package, and ship{" "}
            <span className="text-white">AI capabilities</span> that solve real
            problems.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4 }}
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            id="register"
          >
            <a
              href="https://forms.gle/6mgpbxdarhwd52Uo7"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-register-btn"
              className="group relative inline-flex items-center gap-3 px-7 py-4 rounded-full bg-[#2FCC7C] text-black font-bold text-sm md:text-base tracking-tight hover:shadow-[0_0_40px_rgba(47,204,124,0.65)] transition-all duration-300 hover:-translate-y-0.5"
            >
              <span>Register Now</span>
              <span className="hidden sm:inline text-black/70 font-mono text-xs">
                (Individual · Solo)
              </span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={3} />
              <span className="absolute inset-0 rounded-full ring-2 ring-[#2FCC7C]/40 group-hover:ring-[#2FCC7C]/70 transition-all" />
            </a>

            <a
              href="#skill"
              data-testid="hero-learn-more"
              className="text-sm font-mono uppercase tracking-widest text-zinc-400 hover:text-white transition-colors px-4 py-3"
            >
              Learn how →
            </a>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.6 }}
            className="mt-14 grid grid-cols-3 gap-3 sm:gap-4"
            data-testid="hero-stats"
          >
            {stats.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.label}
                  data-testid={s.testid}
                  className="glass rounded-2xl p-4 md:p-5 hover:border-[#2FCC7C]/40 transition-colors group"
                >
                  <Icon className="w-4 h-4 text-[#2FCC7C] mb-3" />
                  <div className="font-heading text-xl md:text-2xl font-bold text-white">
                    {s.value}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                    {s.label}
                  </div>
                </div>
              );
            })}
          </motion.div>
        </div>

        {/* RIGHT - Interactive cube */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          style={{ y: cubeY, scale: cubeScale }}
          className="relative h-[420px] lg:h-[520px] hidden md:flex items-center justify-center"
          data-testid="hero-cube"
        >
          {/* soft glow behind */}
          <div className="absolute w-[380px] h-[380px] rounded-full bg-[#2FCC7C]/10 blur-3xl" />
          <div className="absolute w-[280px] h-[280px] rounded-full bg-[#2D7BF0]/10 blur-3xl translate-x-20 -translate-y-10" />
          <NeuralBrain mouseX={mouseX} mouseY={mouseY} />
        </motion.div>
      </motion.div>

      {/* bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-[#050505] pointer-events-none z-0" />
    </section>
  );
}
